
const getElementViewportState = require('./element-viewport');

class LazyLoad {

	/* 
	 * UTILITY FUNCTIONS
	 * -----------------
	 */

	_now() {
		var d = new Date();
		return d.getTime();
	}

	/**
	 * @param {string} scrollClass - The class of the elements to be animated.
	 * @param {number} bottom - The bottom threshold for the animation.
	 */
	constructor(instanceSettings = {}) {
		const _defaultSettings = {
			elements_selector: "img",
			container: window,
			threshold: 300,
			throttle: 150,
			data_src: "original",
			data_srcset: "original-set",
			class_loading: "loading",
			class_loaded: "loaded",
			skip_invisible: true,
			callback_load: null,
			callback_error: null,
			callback_set: null,
			callback_processed: null
		};

		this._settings = Object.assign({}, _defaultSettings, instanceSettings);
		this._queryOriginNode = this._settings.container === window ? document : this._settings.container;

		this._previousLoopTime = 0;
		this._loopTimeout = null;
		this._boundHandleScroll = this.handleScroll.bind(this);

		window.addEventListener("resize", this._boundHandleScroll);
		this.update();
	}
	/*
	 * PRIVATE FUNCTIONS
	 * -----------------
	 */

	/**
	 * Check if the element is inside the viewport.
	 * 
	 * @param {HTMLElement} el - The element to check.
	 * @param {HTMLElement} container - The container element.
	 * @param {number} threshold - The threshold for the viewport check.
	 * @returns {boolean} - True if the element is inside the viewport, false otherwise.
	 */
	_isInsideViewport(el, container, threshold) {
		const state = getElementViewportState(el, { top: threshold, bottom: threshold });
		return state === 'visible';
	}

	/**
	 * Update the srcset attribute of the <source> elements inside a <picture> element associated with the given <img> element.
	 * 
	 * @param {HTMLElement} element - The <img> element inside a <picture>.
	 * @param {string} srcsetDataAttribute - The data attribute name (without 'data-') to use for srcset.
	 */
	_setSourcesForPicture(element, srcsetDataAttribute) {
		// break out if the element is not inside a <picture> element
		const parent = element.parentElement;
		if (!parent || parent.tagName !== 'PICTURE') {
			return;
		}

		// Loop through all children of the <picture> element
		Array.from(parent.children).forEach(child => {
			// Only process <source> elements
			if (child.tagName === 'SOURCE') {
				// Get the value of the data attribute for srcset (e.g., data-original-set)
				const sourceSrcset = child.getAttribute('data-' + srcsetDataAttribute);
				// If the data attribute exists, set it as the srcset attribute
				if (sourceSrcset) {
					child.setAttribute('srcset', sourceSrcset);
				}
			}
		});
	}

	/**
	 * Show the element when it appears in the viewport.
	 * Handles loading for IMG, IFRAME, and other elements (e.g., background images).
	   * Adds appropriate classes and triggers callbacks.
	 * 
	 * @param {HTMLElement} element - The element to show.
	 */
	_showOnAppear(element) {
		const settings = this._settings;
		// Define callbacks for load and error events
		let loadCallback, errorCallback = function () { };

		// Error callback: remove loading class, call error callback if provided
		errorCallback = function () {
			element.removeEventListener("load", loadCallback);
			element.removeEventListener("error", errorCallback);
			element.classList.remove(settings.class_loading);
			if (settings.callback_error) {
				settings.callback_error(element);
			}
		};
		// Load callback: remove loading class, add loaded class, call load callback if provided

		loadCallback = function () {
			if (settings === null) {
				return;
			}
			if (settings.callback_load) {
				settings.callback_load(element);
			}
			element.classList.remove(settings.class_loading);
			element.classList.add(settings.class_loaded);
			element.removeEventListener("load", loadCallback);
			element.removeEventListener("error", errorCallback);
		};

		// Attach event listeners for load and error
		element.addEventListener("load", loadCallback);
		element.addEventListener("error", errorCallback);
		element.classList.add(settings.class_loading);

		// Get data attributes and tag name
		const srcsetDataAttribute = settings.data_srcset;
		const srcDataAttribute = settings.data_src;
		const tagName = element.tagName;
		const elementSrc = element.getAttribute('data-' + srcDataAttribute);

		// Handle <img> elements
		if (tagName === "IMG") {
			// Set <source> srcset attributes if inside <picture>
			this._setSourcesForPicture(element, srcsetDataAttribute);

			// Set img srcset if present
			const imgSrcset = element.getAttribute('data-' + srcsetDataAttribute);
			if (imgSrcset) {
				element.setAttribute("srcset", imgSrcset);
			}
			// Set img src if present
			if (elementSrc) {
				element.setAttribute("src", elementSrc);
			}
			return;
		}
		/*
		// Handle <iframe> elements - don't see much use for this
		// but keeping it for posterity
		if (tagName === "IFRAME") {
			if (elementSrc) {
				element.setAttribute("src", elementSrc);
			}
			return;
		}
		*/

		// Handle other elements (e.g., background images)
		if (elementSrc) {
			const img = new Image();
			// When image loads, set as background and dispatch load event
			img.addEventListener('load', function () {
				element.style.backgroundImage = "url(" + elementSrc + ")";
				let e = new Event('load');
				element.dispatchEvent(e);
			}, false);
			// When image fails to load, dispatch error event
			img.addEventListener('error', function () {
				let e = new Event('error');
				element.dispatchEvent(e);
			}, false);

			// Start loading the image
			img.src = elementSrc;
		}

		// Call the set callback if provided
		if (settings.callback_set) {
			settings.callback_set(element);
		}
	}

	/**
	 * Loop through elements and check if they are inside the viewport.
	 * If they are, show them and remove from the list of elements to process.
	 */
	_loopThroughElements() {
		// Get settings and elements to process
		const settings = this._settings;
		const elements = this._elements;
		const elementsLength = (!elements) ? 0 : elements.length;
		const processedIndexes = [];
		let i, element;

		// Iterate over all elements
		for (i = 0; i < elementsLength; i++) {
			element = elements[i];

			// Skip invisible elements if skip_invisible is true
			if (settings.skip_invisible && (element.offsetParent === null)) {
				continue;
			}
			// If element is inside the viewport, show it and mark as processed
			if (this._isInsideViewport(element, settings.threshold)) {
				this._showOnAppear(element);
				processedIndexes.push(i);
				element.wasProcessed = true;
			}
		}
		// Remove processed elements from the elements array
		while (processedIndexes.length > 0) {
			elements.splice(processedIndexes.pop(), 1);

			// Call the processed callback with the remaining elements count
			if (settings.callback_processed) {
				settings.callback_processed(elements.length);
			}
		}
		// If no elements remain, stop the scroll handler
		if (elementsLength === 0) {
			this._stopScrollHandler();
		}
	}

	/**
	 * Removes elements from this._elements that have already been processed.
	 * This keeps the elements array clean and efficient for future operations.
	 */
	_purgeElements() {
		const elements = this._elements;
		const elementsLength = elements.length;
		const elementsToPurge = [];
		let i, element;

		// Collect indexes of elements that have already been processed
		for (i = 0; i < elementsLength; i++) {
			element = elements[i];
			if (element.wasProcessed) {
				elementsToPurge.push(i);
			}
		}
		// Remove processed elements from the elements array, starting from the end
		while (elementsToPurge.length > 0) {
			elements.splice(elementsToPurge.pop(), 1);
		}
	}

	/**
	 * Start the scroll handler to listen for scroll events.
	 */
	_startScrollHandler() {
		if (!this._isHandlingScroll) {
			this._isHandlingScroll = true;
			this._settings.container.addEventListener("scroll", this._boundHandleScroll);
		}
	}

	/**
	 * Stop the scroll handler to stop listening for scroll events.
	 */
	_stopScrollHandler() {
		if (this._isHandlingScroll) {
			this._isHandlingScroll = false;
			this._settings.container.removeEventListener("scroll", this._boundHandleScroll);
		}
	}


	/*
	* PUBLIC FUNCTIONS
	* ----------------
	*/

	handleScroll() {
		// Get the current timestamp
		const now = this._now();
		// Get the throttle interval from settings
		const throttle = this._settings.throttle;
		let remainingTime;

		if (throttle !== 0) {
			// Calculate how much time remains before the next allowed execution
			remainingTime = throttle - (now - this._previousLoopTime);
			// If enough time has passed or the timer was reset, run immediately
			if (remainingTime <= 0 || remainingTime > throttle) {
				// Clear any pending timeout
				if (this._loopTimeout) {
					clearTimeout(this._loopTimeout);
					this._loopTimeout = null;
				}
				// Update the last execution time
				this._previousLoopTime = now;
				// Process elements
				this._loopThroughElements();
			} else if (!this._loopTimeout) {
				// Otherwise, schedule the next execution after the remaining time
				this._loopTimeout = setTimeout(function () {
					this._previousLoopTime = this._now();
					this._loopTimeout = null;
					this._loopThroughElements();
				}.bind(this), remainingTime);
			}
		} else {
			this._loopThroughElements();
		}
	}

	/**
	 * Update the list of elements to be processed.
	 * This function queries the DOM for elements matching the selector and updates the internal list.
	 */
	update() {
		// Converts to array the nodeset obtained querying the DOM from _queryOriginNode with elements_selector
		this._elements = Array.prototype.slice.call(this._queryOriginNode.querySelectorAll(this._settings.elements_selector));
		this._purgeElements();
		this._loopThroughElements();
		this._startScrollHandler();
	}
	/**
	 * Destroy the LazyLoad instance.
	 * This function removes event listeners and cleans up the instance.
	 */
	destroy() {
		window.removeEventListener("resize", this._boundHandleScroll);
		if (this._loopTimeout) {
			clearTimeout(this._loopTimeout);
			this._loopTimeout = null;
		}
		this._stopScrollHandler();
		this._elements = null;
		this._queryOriginNode = null;
		this._settings = null;
	}
}

export default LazyLoad;